<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import axiosInstance from '@/services/interceptor.services';
import { UserService } from '@/services/user.service';
import type { IUser, IUserRole } from '@/types/user.type';

const route = useRoute();
const router = useRouter();
const { t } = useI18n({ useScope: 'global' });
const queryClient = useQueryClient();

const userId = computed(() => route.params.id as string);

// Rôles à exclure de l'assignation dans le contexte "gestion utilisateurs"
// (utilisateurs "guest"/"customer" et rôles applicatifs internes gérés ailleurs).
const EXCLUDED_ROLES = [
  'admin_system',
  'organization_manager',
  'guest',
  'customer',
];

// ── Utilisateur ─────────────────────────────────────────────────────────────────
// `auth/user.role/:userId` ne renvoie QUE les rôles/permissions (pas le profil :
// firstname/lastname/email/phone/photoUrl). `auth/search` n'accepte pas de filtre
// par id (seulement role/restaurantId/email/phone) : on récupère donc la liste
// complète (pagination désactivée, comme `restaurants/search?pagination=false`
// ailleurs dans l'app) et on retrouve l'utilisateur par id côté client. Cette
// page est directement visitable (refresh, lien partagé) donc pas de dépendance
// à un state de navigation. Puis on fusionne avec les rôles/permissions réels.
const {
  data: userSearchRes,
  isPending: isLoadingUser,
  isError: isUserError,
} = useQuery({
  queryKey: computed(() => ['udp-user-search', userId.value]),
  queryFn: async () => {
    const res = await axiosInstance.get(`auth/search?pagination=false`);
    return res.data;
  },
  enabled: computed(() => !!userId.value),
});

const {
  data: userRolesRes,
  isPending: isLoadingRoles,
  isError: isRolesError,
} = useQuery({
  queryKey: computed(() => ['udp-user-roles', userId.value]),
  queryFn: async () => {
    const res = await axiosInstance.get(`auth/user.role/${userId.value}`);
    return res.data;
  },
  enabled: computed(() => !!userId.value),
});

const initialUser = computed<IUser | null>(() => {
  const list = userSearchRes.value?.data ?? [];
  return Array.isArray(list)
    ? (list.find((u: IUser) => u.id === userId.value) ?? null)
    : null;
});

const user = computed<IUser | null>(() => {
  if (!initialUser.value) return null;
  return {
    ...initialUser.value,
    roles: userRolesRes.value?.data?.roles ?? initialUser.value.roles,
  };
});

const isLoading = computed(() => isLoadingUser.value || isLoadingRoles.value);
const isError = computed(() => isUserError.value || isRolesError.value);

// ── Commerces + organisations liés aux rôles de l'utilisateur ──────────────────
const storeIds = computed<string[]>(() => {
  if (!user.value) return [];
  const ids = new Set<string>();
  if (user.value.restaurantId) ids.add(user.value.restaurantId);
  for (const r of user.value.roles ?? []) {
    if (r.store) ids.add(r.store);
  }
  return [...ids];
});

const { data: storesDetail } = useQuery({
  queryKey: computed(() => ['udp-stores', storeIds.value]),
  queryFn: async () => {
    const results = await Promise.all(
      storeIds.value.map(async (sid) => {
        try {
          const res = await axiosInstance.get(`restaurants/get/${sid}`);
          return { storeId: sid, store: res.data?.data ?? res.data };
        } catch {
          return { storeId: sid, store: null };
        }
      }),
    );
    return results;
  },
  enabled: computed(() => storeIds.value.length > 0),
});
function storeDetailFor(storeId: string) {
  return storesDetail.value?.find((s) => s.storeId === storeId)?.store ?? null;
}
function storeName(storeId: string) {
  return storeDetailFor(storeId)?.designation ?? storeId;
}
function goToStoreUsers(storeId: string) {
  router.push(`/commerces/${storeId}?tab=users`);
}

// ── Helpers d'affichage ──────────────────────────────────────────────────────
function userDisplayName(u: IUser | null) {
  if (!u) return '';
  const full = [u.firstname, u.lastname].filter(Boolean).join(' ').trim();
  return full || u.email || 'CNC';
}
function userInitials(u: IUser | null) {
  if (!u) return '?';
  const name = [u.firstname, u.lastname].filter(Boolean).join(' ').trim();
  if (name) {
    const parts = name.split(' ');
    return (parts[0][0] + (parts[1]?.[0] ?? '')).toUpperCase();
  }
  if (u.email) return u.email.slice(0, 2).toUpperCase();
  return 'CN';
}
function userColor(_letter: string) {
  return '#0b1320';
}
function roleCodeColor(code: string) {
  if (!code) return '#64748b';
  const c = code.toLowerCase();
  if (
    c.includes('admin.system') ||
    c.includes('admin_system') ||
    c.includes('admin-system')
  )
    return '#ef4444';
  if (c.includes('admin')) return '#f97316';
  if (c.includes('manager')) return '#3b82f6';
  if (c.includes('staff')) return '#10b981';
  if (c.includes('guest')) return '#94a3b8';
  return '#64748b';
}

function goBack() {
  router.push('/gestion-utilisateurs');
}

// ── Édition des informations utilisateur ───────────────────────────────────────
const isEditing = ref(false);
const editForm = ref({ firstname: '', lastname: '', email: '', phone: '' });
const isSaving = ref(false);
const editError = ref('');
const editSuccess = ref(false);

function resetEditForm() {
  editForm.value = {
    firstname: user.value?.firstname ?? '',
    lastname: user.value?.lastname ?? '',
    email: user.value?.email ?? '',
    phone: user.value?.phone ?? '',
  };
  editError.value = '';
}
function startEdit() {
  resetEditForm();
  editSuccess.value = false;
  isEditing.value = true;
}
function cancelEdit() {
  isEditing.value = false;
  editError.value = '';
}
async function saveEdit() {
  if (!userId.value) return;
  isSaving.value = true;
  editError.value = '';
  try {
    await axiosInstance.put(`auth/update.info/${userId.value}`, {
      firstname: editForm.value.firstname.trim(),
      lastname: editForm.value.lastname.trim(),
      email: editForm.value.email.trim(),
      phone: editForm.value.phone.trim(),
      fullname: [
        editForm.value.firstname.trim(),
        editForm.value.lastname.trim(),
      ]
        .filter(Boolean)
        .join(' '),
    });
    editSuccess.value = true;
    isEditing.value = false;
    await queryClient.invalidateQueries({
      queryKey: ['udp-user-search', userId.value],
    });
    await queryClient.invalidateQueries({ queryKey: ['um-users'] });
  } catch (err: any) {
    const msgs = err?.response?.data?.message;
    editError.value = Array.isArray(msgs)
      ? msgs.join(' · ')
      : msgs || t('userManagement.detail.updateError');
  } finally {
    isSaving.value = false;
  }
}

// ── Photo de profil ─────────────────────────────────────────────────────────────
const isUploadingPhoto = ref(false);
const photoError = ref('');
const photoSuccess = ref(false);
const photoInput = ref<HTMLInputElement | null>(null);

function triggerPhotoPicker() {
  photoInput.value?.click();
}
async function onPhotoSelected(e: Event) {
  const file = (e.target as HTMLInputElement).files?.[0];
  (e.target as HTMLInputElement).value = '';
  if (!file || !userId.value || !user.value) return;
  isUploadingPhoto.value = true;
  photoError.value = '';
  photoSuccess.value = false;
  try {
    await UserService.updateUserPhotoById(
      userId.value,
      file,
      userDisplayName(user.value),
      user.value.restaurantId ?? '',
    );
    photoSuccess.value = true;
    await queryClient.invalidateQueries({
      queryKey: ['udp-user-search', userId.value],
    });
    await queryClient.invalidateQueries({ queryKey: ['um-users'] });
  } catch (err: any) {
    const msgs = err?.response?.data?.message;
    photoError.value = Array.isArray(msgs)
      ? msgs.join(' · ')
      : msgs || t('userManagement.detail.photoError');
  } finally {
    isUploadingPhoto.value = false;
  }
}

// ── Mot de passe ─────────────────────────────────────────────────────────────────
const newPassword = ref('');
const showNewPwd = ref(false);
const isSavingPassword = ref(false);
const passwordError = ref('');
const passwordSuccess = ref(false);

function generateNewPassword() {
  const chars =
    'abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789!@#$%&*';
  let pwd = '';
  const arr = new Uint8Array(14);
  crypto.getRandomValues(arr);
  arr.forEach((b) => {
    pwd += chars[b % chars.length];
  });
  newPassword.value = pwd;
  showNewPwd.value = true;
}
async function savePassword() {
  if (!userId.value || newPassword.value.length < 6) {
    passwordError.value = t('userManagement.detail.passwordTooShort');
    return;
  }
  isSavingPassword.value = true;
  passwordError.value = '';
  passwordSuccess.value = false;
  try {
    await UserService.updateUserPasswordById(userId.value, newPassword.value);
    passwordSuccess.value = true;
    newPassword.value = '';
    showNewPwd.value = false;
  } catch (err: any) {
    const msgs = err?.response?.data?.message;
    passwordError.value = Array.isArray(msgs)
      ? msgs.join(' · ')
      : msgs || t('userManagement.detail.passwordError');
  } finally {
    isSavingPassword.value = false;
  }
}

// ── Rôles : ajout ────────────────────────────────────────────────────────────────
const addRoleOpen = ref(false);
const isAssigningRole = ref(false);
const assignRoleError = ref('');
const assignRoleSuccess = ref(false);

const { data: restaurantsData } = useQuery({
  queryKey: ['udp-restaurants'],
  queryFn: async () => {
    const res = await axiosInstance.get(
      'restaurants/search?pagination=false&sortBy=name&order=ASC',
    );
    return res.data;
  },
  staleTime: 5 * 60_000,
  enabled: computed(() => addRoleOpen.value),
});
const restaurants = computed<any[]>(() => {
  const d = restaurantsData.value;
  const list = d?.data ?? d?.restaurants ?? [];
  return Array.isArray(list) ? list : [];
});

const { data: organizationsData } = useQuery({
  queryKey: ['udp-organizations'],
  queryFn: async () => {
    const res = await axiosInstance.get(
      'organizations/search?pagination=true&pageSize=200&sortBy=name&sortOrder=ASC',
    );
    return res.data;
  },
  staleTime: 5 * 60_000,
  enabled: computed(() => addRoleOpen.value),
});
const organizations = computed<any[]>(() => {
  const d = organizationsData.value;
  const list = d?.data ?? (Array.isArray(d) ? d : []);
  return Array.isArray(list) ? list : [];
});

const { data: rolesData, isPending: rolesLoading } = useQuery({
  queryKey: ['udp-roles'],
  queryFn: async () => {
    const params = new URLSearchParams();
    params.append('pagination', 'false');
    params.append('sortBy', 'label');
    params.append('sortDir', 'ASC');
    const res = await axiosInstance.get(`roles/search?${params.toString()}`);
    return res.data;
  },
  enabled: computed(() => addRoleOpen.value),
  staleTime: 5 * 60_000,
});
const allRoles = computed<any[]>(() =>
  (rolesData.value?.data ?? []).filter(
    (r: any) => !EXCLUDED_ROLES.includes((r.code ?? '').toLowerCase()),
  ),
);

const roleSearch = ref('');
const filteredRoles = computed(() => {
  const q = roleSearch.value.trim().toLowerCase();
  if (!q) return allRoles.value;
  return allRoles.value.filter(
    (r) =>
      (r.label ?? '').toLowerCase().includes(q) ||
      (r.code ?? '').toLowerCase().includes(q),
  );
});
const selectedRoleIds = ref<Set<string>>(new Set());
const assignStoreId = ref('');
const assignOrgId = ref('');

// ── Dropdown rôle personnalisé (recherche + pagination) ────────────────────────
const roleDropdownOpen = ref(false);
const ROLE_PAGE_SIZES = [10, 20, 50] as const;
const rolePageSize = ref<(typeof ROLE_PAGE_SIZES)[number]>(10);
const rolePage = ref(1);
watch([roleSearch, rolePageSize], () => {
  rolePage.value = 1;
});
const roleTotalPages = computed(() =>
  Math.max(1, Math.ceil(filteredRoles.value.length / rolePageSize.value)),
);
const pagedRoles = computed(() => {
  const start = (rolePage.value - 1) * rolePageSize.value;
  return filteredRoles.value.slice(start, start + rolePageSize.value);
});
const selectedRolesLabel = computed(() => {
  const picked = allRoles.value.filter((r: any) =>
    selectedRoleIds.value.has(r.id),
  );
  if (!picked.length) return '';
  if (picked.length === 1) return `${picked[0].label} (${picked[0].code})`;
  return t(
    'userManagement.create.rolesSelected',
    { n: picked.length },
    picked.length,
  );
});
function toggleRole(id: string) {
  const next = new Set(selectedRoleIds.value);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  selectedRoleIds.value = next;
}

// ── Dropdown commerce personnalisé (recherche + pagination, optionnel) ─────────
const storeDropdownOpen = ref(false);
const storeSearch = ref('');
const STORE_PAGE_SIZES = [10, 20, 50] as const;
const storePageSize = ref<(typeof STORE_PAGE_SIZES)[number]>(10);
const storePage = ref(1);
watch([storeSearch, storePageSize], () => {
  storePage.value = 1;
});
const filteredStores = computed(() => {
  const q = storeSearch.value.trim().toLowerCase();
  if (!q) return restaurants.value;
  return restaurants.value.filter((r: any) =>
    (r.designation ?? r.name ?? '').toLowerCase().includes(q),
  );
});
const storeTotalPages = computed(() =>
  Math.max(1, Math.ceil(filteredStores.value.length / storePageSize.value)),
);
const pagedStores = computed(() => {
  const start = (storePage.value - 1) * storePageSize.value;
  return filteredStores.value.slice(start, start + storePageSize.value);
});
const selectedStoreLabel = computed(() => {
  const r = restaurants.value.find((r: any) => r.id === assignStoreId.value);
  return r ? (r.designation ?? r.name) : '';
});
function selectStore(id: string) {
  assignStoreId.value = id;
  storeDropdownOpen.value = false;
}
function clearStore() {
  assignStoreId.value = '';
  storeDropdownOpen.value = false;
}

// ── Dropdown organisation personnalisé (recherche + pagination, optionnel) ────
const orgDropdownOpen = ref(false);
const orgSearch = ref('');
const ORG_PAGE_SIZES = [10, 20, 50] as const;
const orgPageSize = ref<(typeof ORG_PAGE_SIZES)[number]>(10);
const orgPage = ref(1);
watch([orgSearch, orgPageSize], () => {
  orgPage.value = 1;
});
const filteredOrgs = computed(() => {
  const q = orgSearch.value.trim().toLowerCase();
  if (!q) return organizations.value;
  return organizations.value.filter((o: any) =>
    (o.name ?? o.designation ?? '').toLowerCase().includes(q),
  );
});
const orgTotalPages = computed(() =>
  Math.max(1, Math.ceil(filteredOrgs.value.length / orgPageSize.value)),
);
const pagedOrgs = computed(() => {
  const start = (orgPage.value - 1) * orgPageSize.value;
  return filteredOrgs.value.slice(start, start + orgPageSize.value);
});
const selectedOrgLabel = computed(() => {
  const o = organizations.value.find((o: any) => o.id === assignOrgId.value);
  return o ? (o.name ?? o.designation) : '';
});
function selectOrg(id: string) {
  assignOrgId.value = id;
  orgDropdownOpen.value = false;
}
function clearOrg() {
  assignOrgId.value = '';
  orgDropdownOpen.value = false;
}

function openAddRole() {
  selectedRoleIds.value = new Set();
  assignStoreId.value = '';
  assignOrgId.value = '';
  assignRoleError.value = '';
  assignRoleSuccess.value = false;
  roleSearch.value = '';
  rolePage.value = 1;
  storeSearch.value = '';
  storePage.value = 1;
  orgSearch.value = '';
  orgPage.value = 1;
  addRoleOpen.value = true;
}
function closeAddRole() {
  addRoleOpen.value = false;
  roleDropdownOpen.value = false;
  storeDropdownOpen.value = false;
  orgDropdownOpen.value = false;
}
async function submitAssignRole() {
  if (!selectedRoleIds.value.size || !userId.value) {
    assignRoleError.value = t('userManagement.create.noRoleFound');
    return;
  }
  isAssigningRole.value = true;
  assignRoleError.value = '';
  try {
    await UserService.assignUserRolesById(
      userId.value,
      [...selectedRoleIds.value].map((roleId) => ({
        roleId,
        restaurantId: assignStoreId.value || undefined,
        organizationId: assignOrgId.value || undefined,
      })),
    );
    assignRoleSuccess.value = true;
    await queryClient.invalidateQueries({
      queryKey: ['udp-user-roles', userId.value],
    });
    await queryClient.invalidateQueries({ queryKey: ['um-users'] });
  } catch (err: any) {
    const msgs = err?.response?.data?.message;
    assignRoleError.value = Array.isArray(msgs)
      ? msgs.join(' · ')
      : msgs || t('userManagement.detail.roleAssignError');
  } finally {
    isAssigningRole.value = false;
  }
}

// ── Rôles : suppression ──────────────────────────────────────────────────────────
const removingRoleId = ref<string | null>(null);
const removeRoleConfirmId = ref<string | null>(null);
const removeRoleError = ref('');

function askRemoveRole(roleId: string) {
  removeRoleConfirmId.value = roleId;
  removeRoleError.value = '';
}
function cancelRemoveRole() {
  removeRoleConfirmId.value = null;
}
async function confirmRemoveRole(role: IUserRole) {
  if (!userId.value) return;
  removingRoleId.value = role.id;
  removeRoleError.value = '';
  try {
    await axiosInstance.put(`auth/remove.role/${userId.value}`, {
      removals: [{ roleId: role.id, storeId: role.store ?? undefined }],
    });
    removeRoleConfirmId.value = null;
    await queryClient.invalidateQueries({
      queryKey: ['udp-user-roles', userId.value],
    });
    await queryClient.invalidateQueries({ queryKey: ['um-users'] });
  } catch (err: any) {
    const msgs = err?.response?.data?.message;
    removeRoleError.value = Array.isArray(msgs)
      ? msgs.join(' · ')
      : msgs || t('userManagement.detail.roleRemoveError');
  } finally {
    removingRoleId.value = null;
  }
}

// ── Suppression du compte ─────────────────────────────────────────────────────────
const isDeleting = ref(false);
const deleteError = ref('');
const confirmDeleteOpen = ref(false);

async function confirmDelete() {
  if (!userId.value) return;
  isDeleting.value = true;
  deleteError.value = '';
  try {
    await axiosInstance.delete(`auth/delete/${userId.value}`);
    await queryClient.invalidateQueries({ queryKey: ['um-users'] });
    goBack();
  } catch (err: any) {
    const msgs = err?.response?.data?.message;
    deleteError.value = Array.isArray(msgs)
      ? msgs.join(' · ')
      : msgs || t('userManagement.detail.deleteError');
  } finally {
    isDeleting.value = false;
  }
}
</script>

<template>
  <div
    class="udp"
    @click="
      roleDropdownOpen = false;
      storeDropdownOpen = false;
      orgDropdownOpen = false;
    "
  >
    <!-- Breadcrumb -->
    <nav class="udp__bc">
      <button class="udp__bc-link" @click="goBack">
        {{ t('userManagement.title') }}
      </button>
      <i class="pi pi-angle-right udp__bc-sep" />
      <span class="udp__bc-cur">{{ userDisplayName(user) || userId }}</span>
    </nav>

    <div v-if="isLoading" class="udp__skel-wrap">
      <div class="udp__header">
        <div class="udp__skel udp__skel--avatar" />
        <div class="udp__header-info">
          <div
            class="udp__skel udp__skel--line"
            style="width: 12rem; height: 1.25rem"
          />
          <div
            class="udp__skel udp__skel--line"
            style="width: 9rem; margin-top: 0.5rem"
          />
        </div>
      </div>
      <div class="udp__grid">
        <div class="udp__col">
          <div class="udp__card">
            <div
              class="udp__skel udp__skel--line"
              style="width: 8rem; height: 1rem"
            />
            <div class="udp__skel udp__skel--block" style="margin-top: 1rem" />
          </div>
          <div class="udp__card">
            <div
              class="udp__skel udp__skel--line"
              style="width: 6rem; height: 1rem"
            />
            <div
              class="udp__skel udp__skel--block"
              style="margin-top: 1rem; height: 6rem"
            />
          </div>
        </div>
        <div class="udp__col">
          <div class="udp__card">
            <div
              class="udp__skel udp__skel--line"
              style="width: 5rem; height: 1rem"
            />
            <div
              class="udp__skel udp__skel--block"
              style="margin-top: 1rem; height: 5rem"
            />
          </div>
          <div class="udp__card">
            <div
              class="udp__skel udp__skel--line"
              style="width: 7rem; height: 1rem"
            />
            <div
              class="udp__skel udp__skel--block"
              style="margin-top: 1rem; height: 4rem"
            />
          </div>
        </div>
      </div>
    </div>
    <div v-else-if="isError || !user" class="udp__error">
      <i class="pi pi-exclamation-circle" />
      <p>{{ t('userManagement.detail.notFound') }}</p>
      <button class="udp__btn udp__btn--ghost" @click="goBack">
        <i class="pi pi-arrow-left" /> {{ t('userManagement.title') }}
      </button>
    </div>

    <template v-else>
      <!-- Header -->
      <div class="udp__header">
        <button class="udp__back" @click="goBack">
          <i class="pi pi-arrow-left" />
        </button>
        <img
          v-if="user.photoUrl"
          :src="user.photoUrl"
          class="udp__avatar udp__avatar--img"
          alt=""
        />
        <div
          v-else
          class="udp__avatar"
          :style="{ background: userColor(userInitials(user)[0]) }"
        >
          {{ userInitials(user) }}
        </div>
        <div class="udp__header-info">
          <h1 class="udp__name">{{ userDisplayName(user) }}</h1>
          <p v-if="user.email" class="udp__email">{{ user.email }}</p>
          <p v-else-if="user.phone" class="udp__email">{{ user.phone }}</p>
        </div>
        <div class="udp__header-badges">
          <span
            v-for="r in (user.roles ?? []).slice(0, 4)"
            :key="r.id"
            class="udp__role-chip"
            :style="{
              background: roleCodeColor(r.code) + '1a',
              color: roleCodeColor(r.code),
              borderColor: roleCodeColor(r.code) + '40',
            }"
            >{{ r.code }}</span
          >
          <span
            v-if="!(user.roles ?? []).length"
            class="udp__role-chip udp__role-chip--none"
          >
            {{ t('userManagement.noRole') }}
          </span>
        </div>
      </div>

      <div class="udp__grid">
        <div class="udp__col">
          <!-- Section : Informations -->
          <section class="udp__card">
            <div class="udp__card-hd udp__card-hd--between">
              <h3 class="udp__card-title">
                <i class="pi pi-user-edit" />
                {{ t('userManagement.detail.editTitle') }}
              </h3>
              <button
                v-if="!isEditing"
                class="udp__btn udp__btn--ghost udp__btn--sm"
                @click="startEdit"
              >
                <i class="pi pi-pencil" /> {{ t('userManagement.detail.edit') }}
              </button>
            </div>

            <p v-if="editSuccess" class="udp__success udp__success--inline">
              <i class="pi pi-check-circle" />
              {{ t('userManagement.detail.updateSuccess') }}
            </p>

            <div v-if="!isEditing" class="udp__info-view">
              <div class="udp__info-row">
                <span class="udp__info-label">{{
                  t('userManagement.detail.firstname')
                }}</span>
                <span class="udp__info-value">{{ user.firstname || '—' }}</span>
              </div>
              <div class="udp__info-row">
                <span class="udp__info-label">{{
                  t('userManagement.detail.lastname')
                }}</span>
                <span class="udp__info-value">{{ user.lastname || '—' }}</span>
              </div>
              <div class="udp__info-row">
                <span class="udp__info-label">{{
                  t('userManagement.detail.email')
                }}</span>
                <span class="udp__info-value">{{ user.email || '—' }}</span>
              </div>
              <div class="udp__info-row">
                <span class="udp__info-label">{{
                  t('userManagement.detail.phone')
                }}</span>
                <span class="udp__info-value">{{ user.phone || '—' }}</span>
              </div>
            </div>

            <div v-else class="udp__edit-body">
              <div class="udp__field-grid">
                <div class="udp__field">
                  <label class="udp__label">{{
                    t('userManagement.detail.firstname')
                  }}</label>
                  <input v-model="editForm.firstname" class="udp__input" />
                </div>
                <div class="udp__field">
                  <label class="udp__label">{{
                    t('userManagement.detail.lastname')
                  }}</label>
                  <input v-model="editForm.lastname" class="udp__input" />
                </div>
                <div class="udp__field">
                  <label class="udp__label">{{
                    t('userManagement.detail.email')
                  }}</label>
                  <input
                    v-model="editForm.email"
                    type="email"
                    class="udp__input"
                  />
                </div>
                <div class="udp__field">
                  <label class="udp__label">{{
                    t('userManagement.detail.phone')
                  }}</label>
                  <input v-model="editForm.phone" class="udp__input" />
                </div>
              </div>

              <p v-if="editError" class="udp__error udp__error--inline">
                <i class="pi pi-exclamation-circle" /> {{ editError }}
              </p>

              <div class="udp__actions">
                <button class="udp__btn udp__btn--ghost" @click="cancelEdit">
                  {{ t('userManagement.detail.cancel') }}
                </button>
                <button
                  class="udp__btn udp__btn--primary"
                  :disabled="isSaving"
                  @click="saveEdit"
                >
                  <span v-if="isSaving" class="udp__spinner" />
                  {{
                    isSaving
                      ? t('userManagement.detail.saving')
                      : t('userManagement.detail.save')
                  }}
                </button>
              </div>
            </div>
          </section>

          <!-- Section : Rôles -->
          <section class="udp__card">
            <div class="udp__card-hd udp__card-hd--between">
              <h3 class="udp__card-title">
                <i class="pi pi-shield" />
                {{ t('userManagement.detail.store') }}
              </h3>
              <button
                class="udp__btn udp__btn--ghost udp__btn--sm"
                @click="openAddRole"
              >
                <i class="pi pi-plus" />
                {{ t('userManagement.detail.addRole') }}
              </button>
            </div>

            <div v-if="(user.roles ?? []).length" class="udp__roles">
              <div
                v-for="r in user.roles ?? []"
                :key="r.id"
                class="udp__role-row"
              >
                <div class="udp__role-row-top">
                  <span
                    class="udp__role-chip"
                    :style="{
                      background: roleCodeColor(r.code) + '1a',
                      color: roleCodeColor(r.code),
                      borderColor: roleCodeColor(r.code) + '40',
                    }"
                    >{{ r.code }}</span
                  >
                  <button
                    v-if="removeRoleConfirmId !== r.id"
                    class="udp__role-remove"
                    :title="t('userManagement.detail.removeRole')"
                    @click="askRemoveRole(r.id)"
                  >
                    <i class="pi pi-trash" />
                  </button>
                </div>

                <div v-if="r.store" class="udp__role-store">
                  <div class="udp__role-store-info">
                    <span class="udp__role-store-name">{{
                      storeName(r.store)
                    }}</span>
                    <span
                      v-if="storeDetailFor(r.store)?.organization"
                      class="udp__role-org"
                    >
                      <i class="pi pi-building" />
                      {{ storeDetailFor(r.store)?.organization?.name }}
                    </span>
                  </div>
                  <button
                    class="udp__btn udp__btn--ghost udp__btn--sm"
                    @click="goToStoreUsers(r.store)"
                  >
                    <i class="pi pi-arrow-right" />
                    {{ t('userManagement.detail.viewStore') }}
                  </button>
                </div>
                <span v-else class="udp__role-no-store">
                  {{ t('userManagement.detail.store') }} : —
                </span>

                <div
                  v-if="removeRoleConfirmId === r.id"
                  class="udp__role-confirm"
                >
                  <p class="udp__danger-text">
                    {{ t('userManagement.detail.removeRoleConfirm') }}
                  </p>
                  <div class="udp__actions udp__actions--tight">
                    <button
                      class="udp__btn udp__btn--ghost udp__btn--sm"
                      @click="cancelRemoveRole"
                    >
                      {{ t('userManagement.detail.cancel') }}
                    </button>
                    <button
                      class="udp__btn udp__btn--danger udp__btn--sm"
                      :disabled="removingRoleId === r.id"
                      @click="confirmRemoveRole(r)"
                    >
                      <span
                        v-if="removingRoleId === r.id"
                        class="udp__spinner"
                      />
                      {{ t('userManagement.detail.removeRoleConfirmBtn') }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div v-else class="udp__empty">
              {{ t('userManagement.detail.noRoleAssigned') }}
            </div>

            <p v-if="removeRoleError" class="udp__error udp__error--inline">
              <i class="pi pi-exclamation-circle" /> {{ removeRoleError }}
            </p>

            <!-- Ajout d'un rôle -->
            <div v-if="addRoleOpen" class="udp__add-role" @click.stop>
              <div v-if="assignRoleSuccess" class="udp__success">
                <i class="pi pi-check-circle" />
                {{ t('userManagement.create.success') }}
                <button
                  class="udp__btn udp__btn--ghost udp__btn--sm"
                  @click="closeAddRole"
                >
                  {{ t('userManagement.detail.cancel') }}
                </button>
              </div>
              <template v-else>
                <div class="udp__field">
                  <label class="udp__label">{{
                    t('userManagement.create.storeOptional')
                  }}</label>
                  <div class="udp__dropdown-wrap">
                    <button
                      class="udp__dropdown-btn"
                      :class="{ 'udp__dropdown-btn--active': assignStoreId }"
                      @click="storeDropdownOpen = !storeDropdownOpen"
                    >
                      <i class="pi pi-shop" />
                      <span>{{
                        selectedStoreLabel || t('userManagement.create.noStore')
                      }}</span>
                      <i
                        v-if="assignStoreId"
                        class="pi pi-times"
                        @click.stop="clearStore"
                      />
                      <i v-else class="pi pi-chevron-down" />
                    </button>
                    <Transition name="udp-drop">
                      <div v-if="storeDropdownOpen" class="udp__dropdown-panel">
                        <div class="udp__dropdown-search-wrap">
                          <i class="pi pi-search" />
                          <input
                            v-model="storeSearch"
                            class="udp__dropdown-search"
                            :placeholder="
                              t('userManagement.create.searchStore')
                            "
                          />
                        </div>
                        <div class="udp__dropdown-list">
                          <button
                            class="udp__dropdown-item"
                            :class="{
                              'udp__dropdown-item--active': !assignStoreId,
                            }"
                            @click="clearStore"
                          >
                            {{ t('userManagement.create.noStore') }}
                          </button>
                          <button
                            v-for="r in pagedStores"
                            :key="r.id"
                            class="udp__dropdown-item"
                            :class="{
                              'udp__dropdown-item--active':
                                assignStoreId === r.id,
                            }"
                            @click="selectStore(r.id)"
                          >
                            {{ r.designation ?? r.name }}
                          </button>
                          <p
                            v-if="!filteredStores.length"
                            class="udp__dropdown-empty"
                          >
                            {{ t('userManagement.create.noStoreFound') }}
                          </p>
                        </div>
                        <div class="udp__dropdown-footer">
                          <select
                            v-model.number="storePageSize"
                            class="udp__dropdown-pagesize"
                          >
                            <option
                              v-for="ps in STORE_PAGE_SIZES"
                              :key="ps"
                              :value="ps"
                            >
                              {{ ps }} / {{ t('userManagement.perPage') }}
                            </option>
                          </select>
                          <div
                            v-if="storeTotalPages > 1"
                            class="udp__dropdown-pager"
                          >
                            <button
                              class="udp__dropdown-pager-btn"
                              :disabled="storePage === 1"
                              @click="storePage--"
                            >
                              <i class="pi pi-chevron-left" />
                            </button>
                            <span>{{ storePage }} / {{ storeTotalPages }}</span>
                            <button
                              class="udp__dropdown-pager-btn"
                              :disabled="storePage === storeTotalPages"
                              @click="storePage++"
                            >
                              <i class="pi pi-chevron-right" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </Transition>
                  </div>
                </div>

                <div class="udp__field">
                  <label class="udp__label">{{
                    t('userManagement.create.orgOptional')
                  }}</label>
                  <div class="udp__dropdown-wrap">
                    <button
                      class="udp__dropdown-btn"
                      :class="{ 'udp__dropdown-btn--active': assignOrgId }"
                      @click="orgDropdownOpen = !orgDropdownOpen"
                    >
                      <i class="pi pi-building" />
                      <span>{{
                        selectedOrgLabel || t('userManagement.create.noOrg')
                      }}</span>
                      <i
                        v-if="assignOrgId"
                        class="pi pi-times"
                        @click.stop="clearOrg"
                      />
                      <i v-else class="pi pi-chevron-down" />
                    </button>
                    <Transition name="udp-drop">
                      <div v-if="orgDropdownOpen" class="udp__dropdown-panel">
                        <div class="udp__dropdown-search-wrap">
                          <i class="pi pi-search" />
                          <input
                            v-model="orgSearch"
                            class="udp__dropdown-search"
                            :placeholder="t('userManagement.create.searchOrg')"
                          />
                        </div>
                        <div class="udp__dropdown-list">
                          <button
                            class="udp__dropdown-item"
                            :class="{
                              'udp__dropdown-item--active': !assignOrgId,
                            }"
                            @click="clearOrg"
                          >
                            {{ t('userManagement.create.noOrg') }}
                          </button>
                          <button
                            v-for="o in pagedOrgs"
                            :key="o.id"
                            class="udp__dropdown-item"
                            :class="{
                              'udp__dropdown-item--active':
                                assignOrgId === o.id,
                            }"
                            @click="selectOrg(o.id)"
                          >
                            {{ o.name ?? o.designation }}
                          </button>
                          <p
                            v-if="!filteredOrgs.length"
                            class="udp__dropdown-empty"
                          >
                            {{ t('userManagement.create.noOrgFound') }}
                          </p>
                        </div>
                        <div class="udp__dropdown-footer">
                          <select
                            v-model.number="orgPageSize"
                            class="udp__dropdown-pagesize"
                          >
                            <option
                              v-for="ps in ORG_PAGE_SIZES"
                              :key="ps"
                              :value="ps"
                            >
                              {{ ps }} / {{ t('userManagement.perPage') }}
                            </option>
                          </select>
                          <div
                            v-if="orgTotalPages > 1"
                            class="udp__dropdown-pager"
                          >
                            <button
                              class="udp__dropdown-pager-btn"
                              :disabled="orgPage === 1"
                              @click="orgPage--"
                            >
                              <i class="pi pi-chevron-left" />
                            </button>
                            <span>{{ orgPage }} / {{ orgTotalPages }}</span>
                            <button
                              class="udp__dropdown-pager-btn"
                              :disabled="orgPage === orgTotalPages"
                              @click="orgPage++"
                            >
                              <i class="pi pi-chevron-right" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </Transition>
                  </div>
                </div>

                <div class="udp__field">
                  <label class="udp__label"
                    >{{ t('userManagement.create.role') }}
                    <span class="udp__required">*</span></label
                  >
                  <div class="udp__dropdown-wrap">
                    <button
                      class="udp__dropdown-btn"
                      :class="{
                        'udp__dropdown-btn--active': selectedRoleIds.size,
                      }"
                      @click="roleDropdownOpen = !roleDropdownOpen"
                    >
                      <i class="pi pi-shield" />
                      <span>{{
                        selectedRolesLabel || t('userManagement.create.role')
                      }}</span>
                      <i class="pi pi-chevron-down" />
                    </button>
                    <Transition name="udp-drop">
                      <div v-if="roleDropdownOpen" class="udp__dropdown-panel">
                        <div class="udp__dropdown-search-wrap">
                          <i class="pi pi-search" />
                          <input
                            v-model="roleSearch"
                            class="udp__dropdown-search"
                            :placeholder="t('userManagement.create.searchRole')"
                          />
                        </div>
                        <div v-if="rolesLoading" class="udp__role-loading">
                          <i class="pi pi-spin pi-spinner" />
                        </div>
                        <div v-else class="udp__dropdown-list">
                          <button
                            v-for="r in pagedRoles"
                            :key="r.id"
                            class="udp__dropdown-item udp__dropdown-item--checkbox"
                            :class="{
                              'udp__dropdown-item--active': selectedRoleIds.has(
                                r.id,
                              ),
                            }"
                            @click="toggleRole(r.id)"
                          >
                            <i
                              class="pi"
                              :class="
                                selectedRoleIds.has(r.id)
                                  ? 'pi-check-square'
                                  : 'pi-stop'
                              "
                            />
                            {{ r.label }} ({{ r.code }})
                          </button>
                          <p
                            v-if="!filteredRoles.length"
                            class="udp__dropdown-empty"
                          >
                            {{ t('userManagement.create.noRoleFound') }}
                          </p>
                        </div>
                        <div
                          v-if="filteredRoles.length"
                          class="udp__dropdown-footer"
                        >
                          <select
                            v-model.number="rolePageSize"
                            class="udp__dropdown-pagesize"
                          >
                            <option
                              v-for="ps in ROLE_PAGE_SIZES"
                              :key="ps"
                              :value="ps"
                            >
                              {{ ps }} / {{ t('userManagement.perPage') }}
                            </option>
                          </select>
                          <div
                            v-if="roleTotalPages > 1"
                            class="udp__dropdown-pager"
                          >
                            <button
                              class="udp__dropdown-pager-btn"
                              :disabled="rolePage === 1"
                              @click="rolePage--"
                            >
                              <i class="pi pi-chevron-left" />
                            </button>
                            <span>{{ rolePage }} / {{ roleTotalPages }}</span>
                            <button
                              class="udp__dropdown-pager-btn"
                              :disabled="rolePage === roleTotalPages"
                              @click="rolePage++"
                            >
                              <i class="pi pi-chevron-right" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </Transition>
                  </div>
                </div>

                <p v-if="assignRoleError" class="udp__error udp__error--inline">
                  <i class="pi pi-exclamation-circle" /> {{ assignRoleError }}
                </p>

                <div class="udp__actions">
                  <button
                    class="udp__btn udp__btn--ghost"
                    @click="closeAddRole"
                  >
                    {{ t('userManagement.detail.cancel') }}
                  </button>
                  <button
                    class="udp__btn udp__btn--primary"
                    :disabled="isAssigningRole || !selectedRoleIds.size"
                    @click="submitAssignRole"
                  >
                    <span v-if="isAssigningRole" class="udp__spinner" />
                    {{
                      isAssigningRole
                        ? t('userManagement.create.assigning')
                        : t('userManagement.create.assign')
                    }}
                  </button>
                </div>
              </template>
            </div>
          </section>
        </div>
        <div class="udp__col">
          <!-- Section : Photo -->
          <section class="udp__card">
            <div class="udp__card-hd">
              <h3 class="udp__card-title">
                <i class="pi pi-image" />
                {{ t('userManagement.detail.steps.photo') }}
              </h3>
            </div>
            <div class="udp__edit-body udp__photo-body">
              <img
                v-if="user.photoUrl"
                :src="user.photoUrl"
                class="udp__avatar udp__avatar--lg udp__avatar--img"
                alt=""
              />
              <div
                v-else
                class="udp__avatar udp__avatar--lg"
                :style="{ background: userColor(userInitials(user)[0]) }"
              >
                {{ userInitials(user) }}
              </div>
              <input
                ref="photoInput"
                type="file"
                accept="image/*"
                class="udp__file-input"
                @change="onPhotoSelected"
              />
              <button
                class="udp__btn udp__btn--primary"
                :disabled="isUploadingPhoto"
                @click="triggerPhotoPicker"
              >
                <span v-if="isUploadingPhoto" class="udp__spinner" />
                <i v-else class="pi pi-upload" />
                {{
                  isUploadingPhoto
                    ? t('userManagement.detail.uploading')
                    : t('userManagement.detail.uploadPhoto')
                }}
              </button>

              <p v-if="photoSuccess" class="udp__success">
                <i class="pi pi-check-circle" />
                {{ t('userManagement.detail.photoSuccess') }}
              </p>
              <p v-if="photoError" class="udp__error">
                <i class="pi pi-exclamation-circle" /> {{ photoError }}
              </p>
            </div>
          </section>

          <!-- Section : Mot de passe -->
          <section class="udp__card">
            <div class="udp__card-hd">
              <h3 class="udp__card-title">
                <i class="pi pi-lock" />
                {{ t('userManagement.detail.steps.password') }}
              </h3>
            </div>
            <div class="udp__edit-body">
              <div class="udp__field">
                <label class="udp__label">{{
                  t('userManagement.detail.newPassword')
                }}</label>
                <div class="udp__pwd-wrap">
                  <input
                    v-model="newPassword"
                    :type="showNewPwd ? 'text' : 'password'"
                    class="udp__input"
                    placeholder="Min. 6 caractères"
                    autocomplete="new-password"
                  />
                  <button
                    type="button"
                    class="udp__pwd-eye"
                    @click="showNewPwd = !showNewPwd"
                  >
                    <i :class="showNewPwd ? 'pi pi-eye-slash' : 'pi pi-eye'" />
                  </button>
                </div>
                <button
                  type="button"
                  class="udp__gen-btn"
                  @click="generateNewPassword"
                >
                  <i class="pi pi-refresh" />
                  {{ t('userManagement.create.generate') }}
                </button>
              </div>

              <p v-if="passwordSuccess" class="udp__success">
                <i class="pi pi-check-circle" />
                {{ t('userManagement.detail.passwordSuccess') }}
              </p>
              <p v-if="passwordError" class="udp__error">
                <i class="pi pi-exclamation-circle" /> {{ passwordError }}
              </p>

              <div class="udp__actions">
                <button
                  class="udp__btn udp__btn--primary"
                  :disabled="isSavingPassword"
                  @click="savePassword"
                >
                  <span v-if="isSavingPassword" class="udp__spinner" />
                  {{
                    isSavingPassword
                      ? t('userManagement.detail.saving')
                      : t('userManagement.detail.save')
                  }}
                </button>
              </div>
            </div>
          </section>

          <!-- Section : Zone de danger -->
          <section class="udp__card udp__danger-card">
            <div class="udp__card-hd">
              <h3 class="udp__card-title udp__card-title--danger">
                <i class="pi pi-exclamation-triangle" />
                {{ t('userManagement.detail.dangerZone') }}
              </h3>
            </div>
            <div class="udp__edit-body">
              <p class="udp__danger-text">
                {{ t('userManagement.detail.deleteWarning') }}
              </p>

              <p v-if="deleteError" class="udp__error">
                <i class="pi pi-exclamation-circle" /> {{ deleteError }}
              </p>

              <div v-if="!confirmDeleteOpen" class="udp__actions">
                <button
                  class="udp__btn udp__btn--danger"
                  @click="confirmDeleteOpen = true"
                >
                  <i class="pi pi-trash" />
                  {{ t('userManagement.detail.delete') }}
                </button>
              </div>
              <div v-else class="udp__confirm-delete">
                <p class="udp__danger-text">
                  {{ t('userManagement.detail.deleteConfirm') }}
                </p>
                <div class="udp__actions">
                  <button
                    class="udp__btn udp__btn--ghost"
                    @click="confirmDeleteOpen = false"
                  >
                    {{ t('userManagement.detail.cancel') }}
                  </button>
                  <button
                    class="udp__btn udp__btn--danger"
                    :disabled="isDeleting"
                    @click="confirmDelete"
                  >
                    <span v-if="isDeleting" class="udp__spinner" />
                    {{
                      isDeleting
                        ? t('userManagement.detail.deleting')
                        : t('userManagement.detail.deleteConfirmBtn')
                    }}
                  </button>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.udp {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 2.5rem 3rem;
  box-sizing: border-box;
}
@media (max-width: 1023px) {
  .udp {
    padding: 1.5rem;
  }
}

/* Grille principale : gauche 8/12 (Infos + Rôles) + droite 4/12 (Photo + Mot de passe + Danger) */
.udp__grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  align-items: start;
}
@media (min-width: 1024px) {
  .udp__grid {
    grid-template-columns: 8fr 4fr;
  }
}
.udp__col {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  min-width: 0;
}

.udp__bc {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
}
.udp__bc-link {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: 0.8125rem;
  font-family: inherit;
  color: inherit;
}
.udp__bc-link:hover {
  color: #0b1320;
}
.udp__bc-sep {
  font-size: 0.625rem;
}
.udp__bc-cur {
  font-weight: 700;
  color: var(--nxt-on-surface);
}

.udp__loading,
.udp__error {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 4rem 1rem;
  color: var(--nxt-on-surface-muted);
}
.udp__error i {
  font-size: 2rem;
  color: var(--nxt-error);
}
.udp__error--inline {
  padding: 0;
  flex-direction: row;
}
.udp__error--inline i {
  font-size: 1rem;
}

.udp__header {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 1.5rem;
  padding: 1.5rem 1.75rem;
  box-shadow: 0 4px 15px rgba(142, 142, 142, 0.06);
}
.udp__back {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  flex-shrink: 0;
  border: 1px solid var(--nxt-outline-variant);
  background: var(--nxt-surface-low);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9375rem;
  color: var(--nxt-on-surface-variant);
  cursor: pointer;
}
.udp__avatar {
  width: 3.5rem;
  height: 3.5rem;
  min-width: 3.5rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: 700;
}
.udp__avatar--lg {
  width: 5.5rem;
  height: 5.5rem;
  min-width: 5.5rem;
  font-size: 1.75rem;
}
.udp__avatar--img {
  object-fit: cover;
}
.udp__header-info {
  min-width: 0;
  flex: 1;
}
.udp__name {
  margin: 0 0 0.15rem;
  font-size: 1.375rem;
  font-weight: 800;
  color: var(--nxt-on-surface);
}
.udp__email {
  margin: 0;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
}
.udp__header-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
}

.udp__card {
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 1.5rem;
  box-shadow: 0 4px 15px rgba(142, 142, 142, 0.06);
  overflow: hidden;
}
.udp__card-hd {
  padding: 1.125rem 1.5rem;
  border-bottom: 1px solid var(--nxt-outline-variant);
}

/* Skeleton (chargement) */
.udp__skel-wrap .udp__card {
  padding: 1.5rem;
}
.udp__skel {
  background: linear-gradient(
    90deg,
    var(--nxt-surface-low) 25%,
    var(--nxt-surface-mid) 50%,
    var(--nxt-surface-low) 75%
  );
  background-size: 200% 100%;
  animation: udp-shimmer 1.4s infinite;
  border-radius: 0.5rem;
}
.udp__skel--avatar {
  width: 4rem;
  height: 4rem;
  min-width: 4rem;
  border-radius: 50%;
}
.udp__skel--line {
  width: 10rem;
  height: 0.875rem;
}
.udp__skel--block {
  width: 100%;
  height: 3rem;
}
@keyframes udp-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
.udp__card-hd--between {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}
.udp__card-title {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
}
.udp__card-title--danger {
  color: var(--nxt-error);
}

.udp__info-view {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1.25rem 1.5rem;
}
.udp__info-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  font-size: 0.8125rem;
}
.udp__info-label {
  color: var(--nxt-on-surface-muted);
  font-weight: 600;
}
.udp__info-value {
  color: var(--nxt-on-surface);
  font-weight: 700;
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 60%;
}

.udp__roles {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
}
.udp__role-row {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.875rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.75rem;
}
.udp__role-row-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.udp__role-remove {
  width: 1.75rem;
  height: 1.75rem;
  border-radius: 0.5rem;
  border: 1px solid var(--nxt-outline-variant);
  background: var(--nxt-surface-low);
  color: var(--nxt-error);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  cursor: pointer;
}
.udp__role-chip {
  align-self: flex-start;
  font-size: 0.625rem;
  font-weight: 700;
  padding: 0.15rem 0.5rem;
  border-radius: 0.3rem;
  border: 1px solid;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.udp__role-chip--none {
  background: var(--nxt-surface-mid);
  color: var(--nxt-on-surface-muted);
  border-color: var(--nxt-outline-variant);
}
.udp__role-store {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}
.udp__role-store-info {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  min-width: 0;
}
.udp__role-store-name {
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
}
.udp__role-org {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.75rem;
  color: var(--nxt-on-surface-muted);
}
.udp__role-no-store {
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
}
.udp__role-confirm {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid var(--nxt-outline-variant);
}

.udp__empty {
  text-align: center;
  padding: 2rem 1rem;
  color: var(--nxt-on-surface-muted);
  font-size: 0.875rem;
}

.udp__add-role {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  border-top: 1px solid var(--nxt-outline-variant);
}

.udp__btn {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  height: 2.375rem;
  padding: 0 1.125rem;
  border-radius: 0.625rem;
  font-size: 0.8125rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  border: none;
}
.udp__btn--ghost {
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface-variant);
  border: 1px solid var(--nxt-outline-variant);
}
.udp__btn--sm {
  height: 2rem;
  padding: 0 0.75rem;
  font-size: 0.75rem;
}
.udp__btn--primary {
  background: #ffd60a;
  color: #0b1320;
  box-shadow: 0 2px 8px rgba(255, 214, 10, 0.35);
}
.udp__btn--danger {
  background: var(--nxt-error);
  color: #fff;
  box-shadow: 0 2px 8px rgba(220, 38, 38, 0.35);
}
.udp__btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.udp__success {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0 1.5rem;
  padding: 0.75rem 1rem;
  border-radius: 0.75rem;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #0f7b0f;
  background: rgba(16, 181, 16, 0.12);
}
.udp__success--inline {
  margin: 0;
  margin-top: -0.5rem;
}

.udp__edit-body {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
}
.udp__field-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}
@media (max-width: 560px) {
  .udp__field-grid {
    grid-template-columns: 1fr;
  }
}
.udp__photo-body {
  align-items: center;
  text-align: center;
}
.udp__file-input {
  display: none;
}
.udp__field {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}
.udp__label {
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--nxt-on-surface);
}
.udp__required {
  color: var(--nxt-error);
}
.udp__input {
  height: 2.625rem;
  padding: 0 0.875rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.625rem;
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface);
  font-size: 0.875rem;
  font-family: inherit;
  outline: none;
  width: 100%;
  box-sizing: border-box;
}
.udp__input:focus {
  border-color: #ffd60a;
}
.udp__pwd-wrap {
  position: relative;
}
.udp__pwd-eye {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  background: none;
  cursor: pointer;
  color: var(--nxt-on-surface-variant);
}
.udp__gen-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  align-self: flex-start;
  margin-top: 0.375rem;
  padding: 0.25rem 0.625rem;
  border-radius: 0.375rem;
  border: 1px solid var(--nxt-outline-variant);
  background: none;
  font-size: 0.6875rem;
  font-weight: 700;
  font-family: inherit;
  color: #0b1320;
  cursor: pointer;
}
.udp__error {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8125rem;
  color: var(--nxt-error);
  margin: 0;
}
.udp__actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding-top: 0.5rem;
  border-top: 1px solid var(--nxt-outline-variant);
}
.udp__actions--tight {
  padding-top: 0;
  border-top: none;
}
.udp__spinner {
  width: 1rem;
  height: 1rem;
  border-radius: 50%;
  display: inline-block;
  border: 2px solid rgba(11, 19, 32, 0.2);
  border-top-color: #0b1320;
  animation: udp-spin 0.7s linear infinite;
}
@keyframes udp-spin {
  to {
    transform: rotate(360deg);
  }
}

.udp__danger-card {
  border-color: rgba(220, 38, 38, 0.3);
}
.udp__danger-text {
  margin: 0;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
  line-height: 1.5;
}
.udp__confirm-delete {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

/* Dropdown personnalisé (recherche + pagination) — rôle / commerce / organisation */
.udp__dropdown-wrap {
  position: relative;
}
.udp__dropdown-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  height: 2.625rem;
  padding: 0 0.875rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.625rem;
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface-variant);
  font-size: 0.875rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  box-sizing: border-box;
}
.udp__dropdown-btn span {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  text-align: left;
  white-space: nowrap;
}
.udp__dropdown-btn--active {
  border-color: #ffd60a;
  color: var(--nxt-on-surface);
}
.udp__dropdown-panel {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 200;
  background: var(--nxt-card-bg);
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.75rem;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  overflow: hidden;
}
.udp__dropdown-search-wrap {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 0.875rem;
  border-bottom: 1px solid var(--nxt-outline-variant);
}
.udp__dropdown-search {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 0.8125rem;
  font-family: inherit;
  color: var(--nxt-on-surface);
}
.udp__dropdown-list {
  max-height: 200px;
  overflow-y: auto;
  padding: 0.375rem;
}
.udp__dropdown-item {
  display: block;
  width: 100%;
  text-align: left;
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  border: none;
  background: none;
  font-size: 0.8125rem;
  font-family: inherit;
  color: var(--nxt-on-surface);
  cursor: pointer;
}
.udp__dropdown-item:hover {
  background: var(--nxt-surface-mid);
}
.udp__dropdown-item--checkbox {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.udp__dropdown-item--checkbox .pi {
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
}
.udp__dropdown-item--checkbox.udp__dropdown-item--active .pi-check-square {
  color: #ffd60a;
}
.udp__dropdown-item--active {
  background: var(--nxt-surface-mid);
  font-weight: 700;
  color: #0b1320;
}
.udp__dropdown-empty {
  margin: 0;
  padding: 0.75rem;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
  text-align: center;
}
.udp__dropdown-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.5rem 0.625rem;
  border-top: 1px solid var(--nxt-outline-variant);
}
.udp__dropdown-pagesize {
  height: 1.875rem;
  padding: 0 0.5rem;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.5rem;
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface);
  font-size: 0.75rem;
  font-family: inherit;
}
.udp__dropdown-pager {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.75rem;
  color: var(--nxt-on-surface-variant);
}
.udp__dropdown-pager-btn {
  width: 1.5rem;
  height: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 0.375rem;
  background: var(--nxt-card-bg);
  color: var(--nxt-on-surface-variant);
  font-size: 0.6875rem;
  cursor: pointer;
}
.udp__dropdown-pager-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.udp-drop-enter-active,
.udp-drop-leave-active {
  transition:
    opacity 0.15s,
    transform 0.15s;
}
.udp-drop-enter-from,
.udp-drop-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}
.udp__role-loading {
  display: flex;
  justify-content: center;
  padding: 1.5rem;
  color: var(--nxt-on-surface-muted);
}
</style>

<style>
[data-theme='dark'] .udp__header,
[data-theme='dark'] .udp__card {
  background: rgba(20, 28, 41, 0.8);
  border-color: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .udp__back,
[data-theme='dark'] .udp__btn--ghost {
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.7);
  border-color: rgba(255, 255, 255, 0.1);
}
[data-theme='dark'] .udp__role-row,
[data-theme='dark'] .udp__role-remove {
  border-color: rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.05);
}
[data-theme='dark'] .udp__input,
[data-theme='dark'] .udp__dropdown-btn {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
  color: #fff;
}
[data-theme='dark'] .udp__input:focus {
  border-color: #ffd60a;
}
[data-theme='dark'] .udp__dropdown-panel {
  background: #111c2e;
  border-color: rgba(255, 255, 255, 0.1);
}
[data-theme='dark'] .udp__dropdown-item:hover,
[data-theme='dark'] .udp__dropdown-item--active {
  background: rgba(255, 255, 255, 0.08);
}
[data-theme='dark'] .udp__dropdown-pagesize,
[data-theme='dark'] .udp__dropdown-pager-btn {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.7);
}
</style>
